import {
  checkEventDashboardPageOriginResponse,
  eventDashboardPageOriginHeaders,
} from './eventDashboardPageOrigin';
import { isReleaseCompatibleHomepagePost } from './homepageQueries';
import { RELEASE_ID, RELEASE_REQUEST_HEADER, RELEASE_RESPONSE_HEADER } from './identity';

const RELOAD_KEY = 'eb-web:stale-release-reload';
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_WINDOW_MS = 60_000;

export class StaleReleaseError extends Error {
  constructor() {
    super('This page is from an older release. Reload before trying again.');
    this.name = 'StaleReleaseError';
  }
}

export function shouldReloadStaleRelease(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  currentRelease: string = RELEASE_ID,
  targetRelease: string,
  now = Date.now(),
): boolean {
  const transition = `${currentRelease}->${targetRelease}`;
  let previous: { transition?: string; attempts?: number; updatedAt?: number } = {};
  try {
    previous = JSON.parse(storage.getItem(RELOAD_KEY) || '{}');
  } catch {}
  const sameActiveTransition =
    previous.transition === transition &&
    Number.isSafeInteger(previous.attempts) &&
    typeof previous.updatedAt === 'number' &&
    now - previous.updatedAt < RELOAD_WINDOW_MS;
  const attempts = sameActiveTransition ? previous.attempts as number : 0;
  if (attempts >= MAX_RELOAD_ATTEMPTS) return false;
  storage.setItem(RELOAD_KEY, JSON.stringify({ transition, attempts: attempts + 1, updatedAt: now }));
  return true;
}

export const reloadNavigator = {
  reload: () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  },
};

function reloadOnce(targetRelease: string): void {
  if (typeof window === 'undefined') return;
  if (shouldReloadStaleRelease(window.sessionStorage, RELEASE_ID, targetRelease)) {
    reloadNavigator.reload();
  }
}

function isReleaseCompatibleHomepageQuery(input: RequestInfo | URL, method: string): boolean {
  if (typeof window === 'undefined') return false;
  const requestInput = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
  try {
    const url = new URL(requestInput?.url ?? String(input), window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (typeof input === 'string') {
      const rawPath = input.split(/[?#]/, 1)[0].replace(/^https?:\/\/[^/@]+/i, '');
      if (input.startsWith('//') || /[\u0000-\u0020\u007f\\]/.test(input) || rawPath !== url.pathname) return false;
    }
    return isReleaseCompatibleHomepagePost(method, url.pathname);
  } catch {
    return false;
  }
}

export async function releaseFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const requestInput = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
  const method = (init.method || requestInput?.method || 'GET').toUpperCase();
  const headers = eventDashboardPageOriginHeaders(input, init.headers || requestInput?.headers);
  headers.set(RELEASE_REQUEST_HEADER, RELEASE_ID);

  const response = await fetch(input, { ...init, headers });
  checkEventDashboardPageOriginResponse(response);
  const responseRelease = response.headers?.get(RELEASE_RESPONSE_HEADER);
  if (responseRelease && responseRelease !== RELEASE_ID) {
    if (method === 'GET' || method === 'HEAD') reloadOnce(responseRelease);
    else if (!(response.ok && isReleaseCompatibleHomepageQuery(input, method))) {
      throw new StaleReleaseError();
    }
  }
  return response;
}
